import { Box, Typography, useMediaQuery } from '@mui/material';
import * as R from 'ramda';
import { useEffect, useEffectEvent, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button, Skeleton } from '@/components';
import {
  EpisodeFilterDefaults,
  LibraryUrlSearchParams,
  SortByOptions,
} from '@/constants';
import { AppFooter, EpisodeGrid, Link, PageSidebar } from '@/containers';
import { Search } from '@/images';
import { AnalyticsService, EVENT_CONSTANTS, EVENTS } from '@/services';
import {
  selectDefaultRegionFacet,
  selectSessionUserId,
  selectUserCompliance,
  useAppSelector,
  useFetchContentPreferencesQuery,
  useFetchLibraryCategoriesQuery,
  useFetchLibraryEpisodesQuery,
} from '@/store';
import { appStyles } from '@/theme';
import { LibraryFilters } from '@/types';

import {
  CategoryTree,
  FacetSelectors,
  MobileFilters,
  SelectedCategoryInfo,
} from './components';

export const Library = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'));
  const userId = useAppSelector(selectSessionUserId);
  const compliance = useAppSelector(selectUserCompliance);
  const [initialized, setInitialized] = useState(false);

  const { data: contentPreferences } = useFetchContentPreferencesQuery(
    undefined,
    { skip: !userId },
  );

  const defaultRegionFacet = useAppSelector(selectDefaultRegionFacet);

  const [filters, setFilters] = useState<LibraryFilters | null>(null);
  const facets = useMemo(
    () => ({
      region: contentPreferences?.audienceRegions ?? [defaultRegionFacet],
      ...(filters?.mediaType ? { mediaType: filters.mediaType } : {}),
      ...(filters?.audienceFunction
        ? { audienceFunction: filters.audienceFunction }
        : {}),
    }),
    [filters, contentPreferences, defaultRegionFacet],
  );

  const [selectedCategoryInfo, setSelectedCategoryInfo] =
    useState<SelectedCategoryInfo>({
      categorySlug: null,
      subCategorySlug: null,
    });

  const { data: libraryCategories, isFetching: isFetchingLibraryCategories } =
    useFetchLibraryCategoriesQuery({
      facets,
    });

  const { selectedCategory, selectedSubCategory } = useMemo(
    () => ({
      selectedCategory:
        libraryCategories?.entities[selectedCategoryInfo.categorySlug!],
      selectedSubCategory:
        libraryCategories?.entities[selectedCategoryInfo.categorySlug!]
          ?.subCategories.entities[selectedCategoryInfo.subCategorySlug!],
    }),
    [libraryCategories, selectedCategoryInfo],
  );

  const {
    data: episodes,
    isFetching,
    isUninitialized,
  } = useFetchLibraryEpisodesQuery(
    {
      facets,
      params: { sortBy: filters?.sortBy?.[0] },
      librarySlugs:
        selectedCategoryInfo.categorySlug &&
        selectedCategoryInfo.subCategorySlug
          ? [
              `${selectedCategoryInfo.categorySlug}/${selectedCategoryInfo.subCategorySlug}`,
            ]
          : selectedCategoryInfo.categorySlug
            ? [selectedCategoryInfo.categorySlug]
            : (libraryCategories?.ids ?? []), // If no category is selected, fetch episodes for all slugs
      compliance,
    },
    {
      skip: !initialized || isFetchingLibraryCategories, // Wait for categories to load before fetching episodes
    },
  );

  // Initialize filters from URL on first load
  useEffect(() => {
    if (!libraryCategories || initialized) return;

    const urlParams = new URLSearchParams(location.search);
    const {
      [LibraryUrlSearchParams.CATEGORY_SLUG]: categorySlug,
      [LibraryUrlSearchParams.SUB_CATEGORY_SLUG]: subCategorySlug,
    } = Object.fromEntries(urlParams);

    const category = libraryCategories.entities[categorySlug];
    const subCategory = category?.subCategories.entities[subCategorySlug];

    setSelectedCategoryInfo({
      categorySlug: category?.slug ?? null,
      subCategorySlug: subCategory?.slug ?? null,
    });

    // Initialize filters from URL or defaults
    setFilters({
      sortBy: [
        SortByOptions[
          urlParams.get(
            LibraryUrlSearchParams.SORT_BY,
          ) as keyof typeof SortByOptions
        ]?.key ?? EpisodeFilterDefaults.sortBy,
      ],
      mediaType: urlParams
        .get(LibraryUrlSearchParams.MEDIA_TYPE)
        ?.split(',') ?? [EpisodeFilterDefaults.mediaType],
      audienceFunction: urlParams
        .get(LibraryUrlSearchParams.AUDIENCE_FUNCTION)
        ?.split(',') ?? [EpisodeFilterDefaults.audienceFunction],
    });
    setInitialized(true);
  }, [libraryCategories, initialized, location.search]);

  // Keep region filter in sync with default region; keep separate from main filter updates
  useEffect(() => {
    if (!initialized || !!userId) return;
    setFilters(prevFilters => ({
      sortBy: prevFilters?.sortBy ?? [EpisodeFilterDefaults.sortBy],
      mediaType: prevFilters?.mediaType ?? [EpisodeFilterDefaults.mediaType],
      audienceFunction: prevFilters?.audienceFunction ?? [
        EpisodeFilterDefaults.audienceFunction,
      ],
    }));
  }, [initialized, userId]);

  const changeSelectedItem = ({
    categorySlug,
    subCategorySlug,
  }: SelectedCategoryInfo) => {
    if (
      selectedCategoryInfo.categorySlug === categorySlug &&
      selectedCategoryInfo.subCategorySlug === subCategorySlug
    ) {
      setSelectedCategoryInfo({
        categorySlug: null,
        subCategorySlug: null,
      });
      return;
    }

    if (categorySlug || subCategorySlug) {
      AnalyticsService.logEvent(EVENTS.LIBRARY_EVENTS.CATEGORY, {
        ...(categorySlug ? { category: categorySlug } : {}),
        ...(subCategorySlug ? { sub_category: subCategorySlug } : {}),
      });
    }
    setSelectedCategoryInfo({ categorySlug, subCategorySlug });
    document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateUrlWithUserSelections = useEffectEvent(() => {
    if (!filters) return;
    const currentSearch = new URLSearchParams(location.search);
    const newSearch = {
      ...Object.fromEntries(currentSearch),
      [LibraryUrlSearchParams.SORT_BY]:
        filters.sortBy &&
        !R.equals(filters.sortBy, [EpisodeFilterDefaults.sortBy])
          ? filters.sortBy
          : undefined,
      [LibraryUrlSearchParams.MEDIA_TYPE]:
        filters.mediaType &&
        !R.equals(filters.mediaType, [EpisodeFilterDefaults.mediaType])
          ? filters.mediaType
          : undefined,
      [LibraryUrlSearchParams.CATEGORY_SLUG]: selectedCategory?.slug,
      [LibraryUrlSearchParams.SUB_CATEGORY_SLUG]: selectedSubCategory?.slug,
      [LibraryUrlSearchParams.AUDIENCE_FUNCTION]:
        filters.audienceFunction &&
        !R.equals(filters.audienceFunction, [
          EpisodeFilterDefaults.audienceFunction,
        ])
          ? filters.audienceFunction
          : undefined,
    };
    navigate(
      {
        pathname: location.pathname,
        search: new URLSearchParams(
          JSON.parse(JSON.stringify(newSearch)), // This strips out undefined values
        ).toString(),
      },
      { replace: true },
    );
  });

  useEffect(() => {
    // Wait for initialization, otherwise url will get wiped
    if (!libraryCategories || !filters) return;

    updateUrlWithUserSelections();
  }, [libraryCategories, filters, selectedCategory, selectedSubCategory]);

  useEffect(() => {
    if (!isFetching && episodes && filters) {
      AnalyticsService.logEvent(EVENTS.LIBRARY_EVENTS.VIEW, {
        media_type_filter: filters.mediaType,
        sort_order: filters.sortBy,
        episode_count: episodes.length,
        active_category: selectedCategoryInfo,
      });
    }
  }, [episodes, filters, selectedCategoryInfo, isFetching]);

  const updateFilters = (newFilters: LibraryFilters) => {
    AnalyticsService.libraryFilterChange({ prevFilters: filters, newFilters });
    setFilters(newFilters);
  };

  const resetFilters = () => {
    AnalyticsService.logEvent(EVENTS.LIBRARY_EVENTS.RESET);
    setFilters(prevFilters => ({
      sortBy: prevFilters?.sortBy ?? [EpisodeFilterDefaults.sortBy],
      mediaType: [EpisodeFilterDefaults.mediaType],
      audienceFunction: [EpisodeFilterDefaults.audienceFunction],
    }));
    setSelectedCategoryInfo({
      categorySlug: null,
      subCategorySlug: null,
    });
  };

  const hasFiltersApplied =
    !R.equals(filters?.mediaType, [EpisodeFilterDefaults.mediaType]) ||
    !R.equals(filters?.audienceFunction, [
      EpisodeFilterDefaults.audienceFunction,
    ]) ||
    selectedCategory ||
    selectedSubCategory;

  return (
    <Box
      sx={{
        position: 'relative',
        pl: theme => ({ xs: 0, md: theme.dimensions.pageSidebar.width + 'px' }),
      }}>
      <PageSidebar>
        <CategoryTree
          selectedItem={selectedCategoryInfo}
          onSelectItem={changeSelectedItem}
          categories={libraryCategories}
        />
      </PageSidebar>
      <Box sx={appStyles.limitPageWidth}>
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
          }}>
          <Box sx={{ flexShrink: 0 }}>
            {isFetching ? (
              <Skeleton width={150} height={32} />
            ) : (
              <Typography
                variant="h1"
                sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: -1 }}>
                {!isUninitialized && hasFiltersApplied ? (
                  <>
                    {episodes?.length ?? 0} Episodes
                    <Link onClick={resetFilters}>Clear All Filters</Link>
                  </>
                ) : (
                  <>Library</>
                )}
              </Typography>
            )}
          </Box>
          <FacetSelectors filters={filters} onFilter={updateFilters} />
        </Box>
        {isMobile && (
          <MobileFilters
            settings={{
              filters,
              selectedCategoryInfo,
            }}
            onChange={({ filters, selectedCategoryInfo }) => {
              updateFilters(filters!);
              changeSelectedItem(selectedCategoryInfo);
            }}
            onReset={hasFiltersApplied ? resetFilters : undefined}
            categories={libraryCategories}
          />
        )}
        {isFetching && <EpisodeGrid isLoading />}
        {!isFetching &&
          (episodes?.length === 0 ? (
            <Box sx={styles.noResults}>
              <Box
                component="img"
                src={Search}
                sx={{ width: 128, height: 128 }}
              />
              <Typography variant="h4">
                No episodes match these filters
              </Typography>
              <Typography variant="body3">
                Try removing a filter, or clear them all to explore the full
                library.
              </Typography>
              <Button
                onClick={resetFilters}
                variant="primary"
                label="Clear All Filters"
                fullWidth={false}
                sx={{ mt: 0.5 }}
              />
            </Box>
          ) : (
            <EpisodeGrid
              episodes={episodes}
              referrer={{ page: EVENT_CONSTANTS.PAGE.FILTER_LIBRARY }}
            />
          ))}
        <AppFooter compact />
      </Box>
    </Box>
  );
};

const styles = {
  noResults: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    mt: 4,
    gap: 1.5,
  },
};
